import { type JSX } from 'react';
import { type LucideIcon } from 'lucide-react';
import {
  AI_AGENT_INSTRUCTIONS_SLUG,
  EXTENSION_SECURITY_CATALOG_SLUG,
  EXTENSION_SECURITY_CUSTOM_RULES_SLUG,
  SECRET_DETECTION_GITLEAKS_RULES_SLUG,
  POLICIES_AI_INSTRUCTIONS_SLUG,
  POLICIES_ALLOW_BLOCK_SLUG,
} from '@/lib/policyPaths';
import {
  SidebarNavSection,
  type SidebarNavSectionItem,
} from './SidebarNavSection';

interface PoliciesNavGroupProps {
  /** Route the group's parent + "Detection Policies" child navigate to (the policies root). */
  to: string;
  /** Icon for the parent row (Shield). */
  icon: LucideIcon;
  /**
   * Whether to render the "AI Agent Instructions" child. Gated by the
   * `ai_instructions_policy` flag (same flag that exposes the policy group).
   */
  showAiInstructions: boolean;
}

/**
 * PoliciesNavGroup — the "Policies" sidebar section (KIR-3982).
 *
 * Three child sub-pages via {@link SidebarNavSection}: "Detection Policies"
 * (the policies root), "AI Agent Instructions" (`/ai-instructions`, only when
 * `showAiInstructions`), and "Allow / Block Lists" (`/allow-block-lists`).
 */
export function PoliciesNavGroup({
  to,
  icon,
  showAiInstructions,
}: PoliciesNavGroupProps): JSX.Element {
  const items: SidebarNavSectionItem[] = [
    {
      label: 'Detection Policies',
      to,
      // KIR-4184: the Extension Security catalog pages live on sibling
      // sub-routes with no sidebar entry; keep Detection Policies lit there
      // rather than letting the parent "Policies" row grab the pill.
      activePaths: [
        `${to}/${EXTENSION_SECURITY_CATALOG_SLUG}`,
        `${to}/${EXTENSION_SECURITY_CUSTOM_RULES_SLUG}`,
        `${to}/${SECRET_DETECTION_GITLEAKS_RULES_SLUG}`,
      ],
    },
    ...(showAiInstructions
      ? [
          {
            label: 'AI Agent Instructions',
            to: `${to}/${POLICIES_AI_INSTRUCTIONS_SLUG}`,
            // KIR-4184: the "Manage catalog" page lives on a sibling sub-route
            // with no sidebar entry; keep this child lit there instead of
            // letting the parent "Policies" row grab the pill.
            activePaths: [`${to}/${AI_AGENT_INSTRUCTIONS_SLUG}`],
          },
        ]
      : []),
    { label: 'Allow / Block Lists', to: `${to}/${POLICIES_ALLOW_BLOCK_SLUG}` },
  ];

  return (
    <SidebarNavSection label="Policies" to={to} icon={icon} items={items} />
  );
}
